import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Hero } from "@/components/Hero";
import { VideoStory } from "@/components/VideoStory";
import { PartnersMarquee } from "@/components/PartnersMarquee";
import { Stats } from "@/components/Stats";
import { Button } from "@/components/ui/button";

const NMC_IMG = "https://images.pexels.com/photos/9494281/pexels-photo-9494281.jpg";

const PILLARS = [
  {
    eyebrow: "Who We Are",
    title: "A land development partner — with a current focus on entitlement.",
    body: "35+ years across land development, large-scale real estate, and homebuilding. Today, RCCD authors specific plans and provides Executive Management Services to the New Model Colony consortium.",
    to: "/who-we-are",
    cta: "About RCCD",
  },
  {
    eyebrow: "What We Do",
    title: "Specific plans, agency coordination, executive management.",
    body: "Four practice areas — entitlement, executive management, real estate, and agency coordination — built on long-standing relationships with city and county departments across Southern California.",
    to: "/what-we-do",
    cta: "Our practice",
  },
  {
    eyebrow: "What We Have Done",
    title: "5 specific plans · $350M in transactions · 4,000 acres at NMC.",
    body: "Five adopted specific plans across the cities of Ontario and Chino totalling 2,200+ acres. $350 million in completed equity transactions and $50 million pending. Two additional plans in process.",
    to: "/what-we-have-done",
    cta: "Our work",
  },
];

export default function Home() {
  return (
    <>
      <Hero />

      <VideoStory />

      <PartnersMarquee />

      {/* Executive summary section */}
      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <span className="mono text-[10px] uppercase tracking-[0.25em] text-clay">Building your vision</span>
              <h2 className="font-display text-4xl lg:text-6xl text-ink mt-4 leading-[1.02] tracking-[-0.03em] text-balance">
                Your land development partner in <em className="font-light">Southern California.</em>
              </h2>
            </div>
            <p className="lg:col-span-4 lg:col-start-9 text-base lg:text-lg text-foreground/70 leading-relaxed">
              For three decades, RCCD has worked alongside the cities, counties, and builders shaping Southern California — quietly authoring the framework that turns raw land into approved communities.
            </p>
          </div>
        </div>
      </section>

      <Stats />

      {/* NMC feature card */}
      <section className="bg-secondary py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-7 relative aspect-[4/3] lg:aspect-auto overflow-hidden">
              <img src={NMC_IMG} alt="The New Model Colony in the City of Ontario" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 mono text-[10px] uppercase tracking-[0.22em] text-paper/85">Featured program</div>
            </div>
            <div className="lg:col-span-5 flex flex-col justify-center">
              <span className="mono text-[10px] uppercase tracking-[0.25em] text-clay">New Model Colony</span>
              <h3 className="font-display text-3xl lg:text-5xl text-ink mt-4 leading-[1.05] tracking-[-0.02em]">
                A 4,000-acre community in the <em className="font-light">City of Ontario.</em>
              </h3>
              <p className="mt-6 text-base text-foreground/75 leading-relaxed">
                RCCD provides ongoing Executive Management Services in support of the NMC consortium of builders and developers. When complete, the program will house over <strong className="text-ink">30,000 residents</strong>, with schools, shopping centers, a college, office park, and industrial areas.
              </p>
              <p className="mt-4 text-base text-foreground/75 leading-relaxed">
                Public-record achievements include the award-winning Mill Creek Regional Wetlands, tens of miles of storm drains, sewer lines, and regional streets, a bridge, a fire station, and a new elementary school as part of an 8-school mitigation agreement.
              </p>
              <Button asChild className="mt-8 self-start h-12 rounded-none bg-ink text-paper hover:bg-clay mono text-[11px] uppercase tracking-[0.22em] px-6">
                <Link to="/what-we-have-done">
                  See our work <ArrowUpRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 3 pillars → deeper pages */}
      <section className="bg-paper py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.to}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <Link to={p.to} className="group flex flex-col h-full bg-secondary border border-border p-8 lg:p-10 hover:border-clay hover:bg-paper transition-colors">
                  <div className="flex items-center justify-between mono text-[10px] uppercase tracking-[0.22em] text-clay">
                    <span>{p.eyebrow}</span>
                    <span>{String(i+1).padStart(2,'0')}</span>
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl text-ink mt-5 leading-tight tracking-[-0.01em]">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-base text-foreground/75 leading-relaxed flex-1">{p.body}</p>
                  <div className="mt-8 inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-ink group-hover:text-clay">
                    {p.cta} <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA strip */}
      <section className="bg-ink text-paper py-20 lg:py-28 grain">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="mono text-[10px] uppercase tracking-[0.25em] text-primary-glow">Get in touch</span>
              <h2 className="font-display text-4xl lg:text-6xl mt-4 leading-[1.02] tracking-[-0.03em] text-balance">
                Have a parcel or plan in mind? <em className="font-light text-paper/75">Let's talk.</em>
              </h2>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button asChild className="h-12 rounded-none bg-paper text-ink hover:bg-primary-glow mono text-[11px] uppercase tracking-[0.22em] px-6">
                <Link to="/contact-us">
                  Contact RCCD <ArrowUpRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <div className="mt-5 mono text-[10px] uppercase tracking-[0.22em] text-paper/55">
                or call <a href="tel:7146374405" className="text-paper hover:text-primary-glow">(714) 637-4405</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
